import { useEffect, useRef, useState } from "react";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Package, Plus, Search, AlertTriangle, ArrowRightLeft, Pencil, Trash2, ImagePlus, X, Loader2, Star } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 5;
const imgUrl = (p) => (!p ? "" : p.startsWith("http") ? p : `${BACKEND}${p}`);
const empty = { name: "", category: "", cost: "", price: "", brand: "", compatibleModel: "", internalCode: "", barcode: "", location: "", min_quantity: 0, quantity: 0, visibility: "PDV + Loja Virtual", notes: "", featured: false, photos: [], ncm: "", cfop: "5405", cst: "500" };

export default function Inventory() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [cats, setCats] = useState({ categories: [], visibilities: [] });
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [edit, setEdit] = useState(null);
  const [mov, setMov] = useState(null);
  const canManage = hasPerm("inventory.manage");

  const load = async () => {
    const p = new URLSearchParams(); if (cat) p.set("category", cat); if (q) p.set("q", q);
    const [i, s] = await Promise.all([api.get(`/inventory?${p}`), api.get("/inventory/stats")]);
    setItems(i.data); setStats(s.data);
  };
  useEffect(() => { api.get("/inventory/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [cat, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (p) => {
    if (!window.confirm(`Excluir o produto "${p.name}"? Ele deixará de aparecer no estoque, PDV e loja.`)) return;
    try { await api.delete(`/inventory/${p.id}`); toast.success("Produto excluído"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const shown = lowOnly ? items.filter((p) => p.quantity <= (p.min_quantity || 0)) : items;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl font-display font-extrabold tracking-tight text-white">Estoque Inteligente</h1>
          <p className="text-zinc-400 mt-1">Fonte única de produtos · alimenta PDV e Loja Virtual.</p></div>
        {canManage && <Button data-testid="new-product-btn" onClick={() => setEdit({ ...empty })} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}><Plus size={16} className="mr-2" />Novo Produto</Button>}
      </div>

      {stats && <div className="grid grid-cols-3 gap-4">
        <Kpi label="Total de produtos" value={stats.total} />
        <Kpi label="Estoque baixo" value={stats.low} amber />
        <Kpi label="Sem estoque" value={stats.out} red />
      </div>}

      {stats && (stats.low + stats.out) > 0 && (
        <div data-testid="low-stock-banner" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 flex items-center gap-2 text-sm text-amber-200">
          <AlertTriangle size={16} className="shrink-0" />
          <span>{stats.out} sem estoque e {stats.low} com estoque baixo.</span>
          <button data-testid="low-stock-see" onClick={() => setLowOnly(true)} className="underline ml-1 hover:text-amber-100">Ver itens</button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Select value={cat || "all"} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="cat-filter" className="i w-52"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent className="bg-[#121217] border-gold/20 text-white">
            <SelectItem value="all" className="focus:bg-gold/10">Todas categorias</SelectItem>
            {cats.categories.map((c) => <SelectItem key={c} value={c} className="focus:bg-gold/10">{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[220px]"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, código, código de barras, modelo compatível..." className="i pl-9" /></div>
        <button data-testid="inv-low-filter" onClick={() => setLowOnly((v) => !v)} className={`px-3 rounded-lg text-sm h-10 border whitespace-nowrap ${lowOnly ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "border-white/10 text-zinc-400 hover:text-white"}`}>Abaixo do mínimo</button>
      </div>

      <div className="grid gap-2">
        {shown.length === 0 && <p className="text-sm text-zinc-500">Nenhum produto{lowOnly ? " abaixo do mínimo" : ""}.</p>}
        {shown.map((p) => {
          const low = p.quantity > 0 && p.quantity <= (p.min_quantity || 0), out = p.quantity <= 0;
          return (
            <div key={p.id} data-testid={`inv-row-${p.id}`} className="alpha-card p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium flex items-center gap-2">{p.name}
                  {out && <span className="text-[10px] text-red-400 inline-flex items-center gap-1"><AlertTriangle size={10} />sem estoque</span>}
                  {low && <span className="text-[10px] text-amber-400 inline-flex items-center gap-1"><AlertTriangle size={10} />baixo</span>}
                </div>
                <div className="text-xs text-zinc-500">{p.category} · {p.visibility} {p.brand ? `· ${p.brand}` : ""}</div>
              </div>
              <div className="text-right"><div className="text-gold-bright font-mono-kpi">{money(p.price)}</div><div className="text-xs text-zinc-500">qtd: {p.quantity}</div></div>
              {canManage && <>
                <button data-testid={`inv-move-${p.id}`} onClick={() => setMov(p)} className="text-zinc-400 hover:text-gold-bright" title="Movimentar"><ArrowRightLeft size={16} /></button>
                <button data-testid={`inv-edit-${p.id}`} onClick={() => setEdit(p)} className="text-zinc-400 hover:text-gold-bright" title="Editar"><Pencil size={16} /></button>
                <button data-testid={`inv-del-${p.id}`} onClick={() => remove(p)} className="text-zinc-400 hover:text-red-400" title="Excluir"><Trash2 size={16} /></button>
              </>}
            </div>
          );
        })}
      </div>

      {edit && <ProductDialog product={edit} cats={cats} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {mov && <MovementDialog product={mov} onClose={() => setMov(null)} onSaved={() => { setMov(null); load(); }} />}
    </div>
  );
}

function ProductDialog({ product, cats, onClose, onSaved }) {
  const isNew = !product.id;
  const [f, setF] = useState({ ...empty, ...product });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (files.length === 0) return;
    if ((f.photos?.length || 0) + files.length > MAX_PHOTOS) { toast.error(`Máximo de ${MAX_PHOTOS} fotos.`); return; }
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/inventory/upload", fd);
        setF((prev) => ({ ...prev, photos: [...(prev.photos || []), data.url] }));
      }
      toast.success("Foto enviada");
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const removePhoto = (url) => setF((prev) => ({ ...prev, photos: (prev.photos || []).filter((p) => p !== url) }));
  const save = async () => {
    if (!f.name || !f.category || f.cost === "" || f.price === "") { toast.error("Nome, categoria, custo e preço são obrigatórios."); return; }
    const qty = parseInt(f.quantity) || 0;
    const payload = { ...f, cost: parseFloat(f.cost) || 0, price: parseFloat(f.price) || 0, min_quantity: parseInt(f.min_quantity) || 0, quantity: qty };
    try {
      if (isNew) {
        await api.post("/inventory", payload);
      } else {
        await api.put(`/inventory/${product.id}`, payload);
        if (qty !== (product.quantity ?? 0)) {
          await api.post(`/inventory/${product.id}/movement`, { type: "Ajuste", quantity: qty, reason: "Ajuste manual via edição" });
        }
      }
      toast.success("Produto salvo"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-2xl" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Novo Produto" : "Editar Produto"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
          <div className="sm:col-span-2 border-b border-white/10 pb-3">
            <Label className="text-xs text-zinc-400 mb-1.5 block">Fotos p/ Loja Virtual (até {MAX_PHOTOS})</Label>
            <div className="flex flex-wrap gap-2">
              {(f.photos || []).map((url) => (
                <div key={url} className="relative h-20 w-20 rounded-lg overflow-hidden border border-white/10">
                  <img src={imgUrl(url)} alt="foto" className="h-full w-full object-cover" />
                  <button type="button" data-testid="prod-remove-photo" onClick={() => removePhoto(url)} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600"><X size={12} /></button>
                </div>
              ))}
              {(f.photos?.length || 0) < MAX_PHOTOS && (
                <button type="button" data-testid="prod-add-photo" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="h-20 w-20 rounded-lg border border-dashed border-white/20 text-zinc-400 flex flex-col items-center justify-center hover:border-gold/50 hover:text-gold-bright transition-colors">
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <><ImagePlus size={18} /><span className="text-[10px] mt-1">Adicionar</span></>}
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} className="hidden" data-testid="prod-file-input" />
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer mt-3">
              <input type="checkbox" data-testid="prod-featured" checked={!!f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="accent-[#D4AF37]" />
              <Star size={13} className="text-gold-bright" /> Destaque na Loja Virtual (aparece primeiro)
            </label>
          </div>
          <div className="sm:col-span-2"><L t="Nome *"><Input data-testid="prod-name" value={f.name} onChange={set("name")} className="i" /></L></div>
          <L t="Categoria *"><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="prod-cat" className="i"><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent className="bg-[#121217] border-gold/20 text-white">{cats.categories.map((c) => <SelectItem key={c} value={c} className="focus:bg-gold/10">{c}</SelectItem>)}</SelectContent></Select></L>
          <L t="Disponibilidade"><Select value={f.visibility} onValueChange={(v) => setF({ ...f, visibility: v })}><SelectTrigger data-testid="prod-vis" className="i"><SelectValue /></SelectTrigger><SelectContent className="bg-[#121217] border-gold/20 text-white">{cats.visibilities.map((v) => <SelectItem key={v} value={v} className="focus:bg-gold/10">{v}</SelectItem>)}</SelectContent></Select></L>
          <L t="Custo (R$) *"><Input type="number" value={f.cost} onChange={set("cost")} className="i" /></L>
          <L t="Preço de venda (R$) *"><Input type="number" value={f.price} onChange={set("price")} className="i" /></L>
          <L t="Marca"><Input value={f.brand} onChange={set("brand")} className="i" /></L>
          <L t="Modelo compatível"><Input value={f.compatibleModel} onChange={set("compatibleModel")} className="i" /></L>
          <L t="Código interno"><Input value={f.internalCode} onChange={set("internalCode")} className="i" /></L>
          <L t="Código de barras"><Input value={f.barcode} onChange={set("barcode")} className="i" /></L>
          <L t="Localização"><Input value={f.location} onChange={set("location")} className="i" /></L>
          <L t="Estoque mínimo (alerta)"><Input type="number" value={f.min_quantity} onChange={set("min_quantity")} className="i" /></L>
          <L t={isNew ? "Quantidade inicial" : "Quantidade em estoque"}><Input data-testid="prod-qty" type="number" value={f.quantity} onChange={set("quantity")} className="i" /></L>
          <div className="sm:col-span-2 border-t border-white/10 pt-3 mt-1"><span className="text-xs uppercase tracking-wider text-gold-bright/80">Dados fiscais (NFC-e)</span></div>
          <L t="NCM"><Input data-testid="prod-ncm" value={f.ncm} onChange={set("ncm")} className="i" placeholder="Ex: 85176259" /></L>
          <L t="CFOP"><Input data-testid="prod-cfop" value={f.cfop} onChange={set("cfop")} className="i" placeholder="Ex: 5102" /></L>
          <L t="CST / CSOSN"><Input data-testid="prod-cst" value={f.cst} onChange={set("cst")} className="i" placeholder="Ex: 102" /></L>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="prod-save" onClick={save} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MovementDialog({ product, onClose, onSaved }) {
  const [f, setF] = useState({ type: "Entrada manual", quantity: 1, reason: "" });
  const save = async () => {
    try { await api.post(`/inventory/${product.id}/movement`, { ...f, quantity: parseInt(f.quantity) || 0 }); toast.success("Movimentação registrada"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-md" data-testid="movement-dialog">
        <DialogHeader><DialogTitle className="font-display">Movimentar · {product.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <L t="Tipo"><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="mov-type" className="i"><SelectValue /></SelectTrigger><SelectContent className="bg-[#121217] border-gold/20 text-white">{["Compra", "Venda", "Ajuste", "Garantia", "Perda", "Entrada manual"].map((t) => <SelectItem key={t} value={t} className="focus:bg-gold/10">{t}</SelectItem>)}</SelectContent></Select></L>
          <L t={f.type === "Ajuste" ? "Nova quantidade (absoluta)" : "Quantidade"}><Input data-testid="mov-qty" type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} className="i" /></L>
          <L t="Motivo"><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="i" rows={2} /></L>
          <div className="text-xs text-zinc-500">Estoque atual: {product.quantity}</div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="mov-save" onClick={save} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Registrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Kpi = ({ label, value, amber, red }) => (<div className="alpha-card p-5"><div className={`text-3xl font-mono-kpi font-extrabold ${red ? "text-red-400" : amber ? "text-amber-400" : "text-white"}`}>{value}</div><div className="text-xs text-zinc-500 mt-1">{label}</div></div>);
const L = ({ t, children }) => (<div><Label className="text-xs text-zinc-400 mb-1.5 block">{t}</Label>{children}</div>);
